import {
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import type { AlphaEntity } from '@backstage/catalog-model/alpha';
import type { UnprocessedEntity } from '@backstage/plugin-catalog-unprocessed-entities-common';
import { POLE_IDS } from '@internal/backstage-plugin-poles-common';

export type CatalogIssueCode =
  | 'missing-owner'
  | 'dangling-owner'
  | 'missing-system-or-domain'
  | 'processing-error'
  | 'rejected-location'
  | 'never-processed'
  | 'not-routed'
  | 'missing-pole-tag';

export type CatalogIssue = {
  code: CatalogIssueCode;
  label: string;
};

export type CatalogHealthEntry = {
  entity: Entity;
  issues: CatalogIssue[];
  // Set when the entity failed its first processing and so isn't in the
  // catalog: it has no entity page to link to.
  unprocessed?: true;
};

export type DomainConfigForHealthCheck = Record<
  string,
  { systems: Array<{ systemId: string }> }
>;

export type CategoryForHealthCheck = { path: string };

const KINDS_WITHOUT_OWNER_CONCEPT = new Set(['group', 'user', 'domain', 'location']);
const KINDS_REQUIRING_SYSTEM = new Set(['component', 'api', 'resource']);

// Start of the error GitlabGroupVisibilityProcessor throws for a Location
// pointing outside the scanned GitLab groups
// (packages/backend/src/modules/gitlabGroupVisibilityFilter.ts). The catalog
// wraps it in its own message, hence the search rather than a prefix match.
const REJECTED_LOCATION_PATTERN = /Rejected Location target (\S+?):? /;

function rejectedLocationIssue(message: string): CatalogIssue | undefined {
  const match = message.match(REJECTED_LOCATION_PATTERN);
  if (!match) {
    return undefined;
  }
  return {
    code: 'rejected-location',
    label: `Location hors des groupes scannés : ${match[1]}`,
  };
}

function buildEntityRefSet(entities: Entity[]): Set<string> {
  return new Set(entities.map(entity => stringifyEntityRef(entity)));
}

function buildReachableSystemIds(
  domainConfig: DomainConfigForHealthCheck,
  categories: CategoryForHealthCheck[],
): Set<string> {
  const ids = new Set<string>();
  Object.values(domainConfig).forEach(domain => {
    domain.systems.forEach(system => ids.add(system.systemId));
  });
  categories.forEach(category => {
    const match = category.path.match(/^\/system\/(.+)$/);
    if (match) {
      ids.add(match[1]);
    }
  });
  return ids;
}

function computeIssuesForEntity(
  entity: Entity,
  entityRefs: Set<string>,
  reachableSystemIds: Set<string>,
): CatalogIssue[] {
  const issues: CatalogIssue[] = [];
  const kind = entity.kind.toLowerCase();
  const owner = entity.spec?.owner as string | undefined;

  if (!KINDS_WITHOUT_OWNER_CONCEPT.has(kind) && !owner) {
    issues.push({ code: 'missing-owner', label: 'Propriétaire manquant' });
  }

  if (owner) {
    const ownerRef = stringifyEntityRef(
      parseEntityRef(owner, {
        defaultKind: 'Group',
        defaultNamespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
      }),
    );
    if (!entityRefs.has(ownerRef)) {
      issues.push({ code: 'dangling-owner', label: 'Propriétaire introuvable' });
    }
  }

  if (KINDS_REQUIRING_SYSTEM.has(kind) && !entity.spec?.system) {
    issues.push({
      code: 'missing-system-or-domain',
      label: 'Système non renseigné',
    });
  }
  if (kind === 'system' && !entity.spec?.domain) {
    issues.push({
      code: 'missing-system-or-domain',
      label: 'Domaine non renseigné',
    });
  }

  if (kind === 'component') {
    const tags = entity.metadata.tags ?? [];
    const polesTagSet: readonly string[] = POLE_IDS;
    if (!tags.some(tag => polesTagSet.includes(tag))) {
      issues.push({ code: 'missing-pole-tag', label: 'Aucun tag de pôle' });
    }
  }

  const statusItems = (entity as AlphaEntity).status?.items ?? [];
  statusItems
    .filter(item => item.level === 'error')
    .forEach(item => {
      issues.push(
        rejectedLocationIssue(item.message) ?? {
          code: 'processing-error',
          label: `Erreur de traitement : ${item.message}`,
        },
      );
    });

  if (kind === 'system' && !reachableSystemIds.has(entity.metadata.name)) {
    issues.push({
      code: 'not-routed',
      label: 'Non accessible depuis le menu Accueil',
    });
  }

  return issues;
}

export function computeCatalogHealth(
  entities: Entity[],
  domainConfig: DomainConfigForHealthCheck,
  categories: CategoryForHealthCheck[],
): CatalogHealthEntry[] {
  const entityRefs = buildEntityRefSet(entities);
  const reachableSystemIds = buildReachableSystemIds(domainConfig, categories);

  return entities
    .map(entity => ({
      entity,
      issues: computeIssuesForEntity(entity, entityRefs, reachableSystemIds),
    }))
    .filter(entry => entry.issues.length > 0);
}

// Entities whose first processing failed never reach the catalog, so
// getEntities() can't see them — notably a Location rejected by
// GitlabGroupVisibilityProcessor as soon as it's declared. They come from
// the unprocessed-entities module's "failed" list instead.
export function computeUnprocessedHealth(
  unprocessedEntities: UnprocessedEntity[],
): CatalogHealthEntry[] {
  return unprocessedEntities.map(unprocessed => {
    const errors = unprocessed.errors ?? [];
    const issues = errors.map(
      error =>
        rejectedLocationIssue(error.message) ?? {
          code: 'never-processed' as const,
          label: `Jamais importée : ${error.message}`,
        },
    );
    return {
      entity: unprocessed.unprocessed_entity,
      issues: issues.length
        ? issues
        : [{ code: 'never-processed', label: 'Jamais importée' }],
      unprocessed: true,
    };
  });
}
